import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { SelectButton } from 'primereact/selectbutton';
import { ServiceStatisticsHourlyDTO, ServiceStatisticsPeakHourDTO } from '../../dto/ServiceStatisticsDTO';
import HeatmapChart from './HeatmapChart';
import HourlyChart from './HourlyChart';
import InfoTip from '../../components/novarium/InfoTip';
import MeasureSelector from './MeasureSelector';
import {
    buildCriticalHours, buildHeat, buildHourlyTotal, formatDecimal, formatHour, formatMinutes, Measure,
} from './statisticsUtils';
import { weekdayNames } from './chartText';
import { CRITICAL_HOURS_MIN_ATTENTIONS } from './alertThresholds';

interface HourlyTabProps {
    hourly: ServiceStatisticsHourlyDTO;
    peakHour: ServiceStatisticsPeakHourDTO;
    /** El «Medir por» compartido con la evolución. */
    measure: Measure;
    onMeasureChange: (measure: Measure) => void;
}

/** El mismo dato en dos vistas: el día de la semana por hora (mapa de calor) o solo la hora (curva). */
type HourlyView = 'heat' | 'hour';
const VIEWS: HourlyView[] = ['heat', 'hour'];
const VIEW_LABEL_ID: Record<HourlyView, string> = {
    heat: 'ServicesStatisticsHourlyViewHeat',
    hour: 'ServicesStatisticsHourlyViewHour',
};
const VIEW_TITLE_ID: Record<HourlyView, string> = {
    heat: 'ServicesStatisticsHeatTitleSection',
    hour: 'ServicesStatisticsHourlyTitle',
};

const MEASURE_NAME_ID: Record<Measure, string> = {
    attentions: 'ServicesStatisticsMeasureAttentions',
    wait: 'ServicesStatisticsMeasureWait',
    service: 'ServicesStatisticsMeasureService',
};
const HEAT_NOTE_ID: Record<Measure, string> = {
    attentions: 'ServicesStatisticsHeatNoteAttentions',
    wait: 'ServicesStatisticsHeatNoteWait',
    service: 'ServicesStatisticsHeatNoteService',
};

/**
 * Pestaña «Horarios»: un solo bloque. Arriba, «Medir por» (el mismo de la evolución) y «Ver» (por día y hora, o por
 * hora); después, una frase según la medida (los peores horarios con la espera, la hora pico con las atenciones; con la
 * duración no hay frase) y un solo gráfico: el mapa de calor del día de la semana por hora, o la curva por hora con todo
 * lo filtrado sumado.
 */
export default function HourlyTab({ hourly, peakHour, measure, onMeasureChange }: HourlyTabProps) {
    const intl = useIntl();
    const [view, setView] = useState<HourlyView>('heat');

    const points = useMemo(() => buildHourlyTotal(hourly), [hourly]);
    const heat = useMemo(() => buildHeat(hourly), [hourly]);
    const criticalHours = useMemo(() => buildCriticalHours(hourly), [hourly]);
    const criticalText = useMemo(() => {
        const days = weekdayNames(intl);
        const lowerCase = intl.locale.startsWith('es');
        return criticalHours.map(item => {
            const day = days[item.dayOfWeek - 1];
            return intl.formatMessage({ id: item.toHour - item.fromHour > 1 ? 'ServicesStatisticsCriticalRange' : 'ServicesStatisticsCriticalHour' }, {
                day: lowerCase ? day.toLowerCase() : day,
                from: item.fromHour,
                to: item.toHour,
                minutes: formatMinutes(item.avgWait),
            });
        }).join(', ');
    }, [intl, criticalHours]);

    const measureName = intl.formatMessage({ id: MEASURE_NAME_ID[measure] });
    // Cómo leer el gráfico: no se ve, lo lee el lector de pantalla (la leyenda y los ejes ya lo dicen a quien mira)
    const description = intl.formatMessage({
        id: view === 'heat' ? HEAT_NOTE_ID[measure]
            : measure === 'attentions' ? 'ServicesStatisticsHourlyHintDay' : 'ServicesStatisticsHourlyHintAttention',
    });
    const viewOptions = VIEWS.map(item => ({ value: item, label: intl.formatMessage({ id: VIEW_LABEL_ID[item] }) }));

    const sentence = measure === 'wait' && criticalText
        ? { text: intl.formatMessage({ id: 'ServicesStatisticsCriticalHours' }, { list: criticalText }),
            hint: intl.formatMessage({ id: 'ServicesStatisticsCriticalHoursHint' }, { min: CRITICAL_HOURS_MIN_ATTENTIONS }) }
        : measure === 'attentions' && peakHour.hour !== null
            ? { text: intl.formatMessage({ id: 'ServicesStatisticsPeakHour' },
                { hour: formatHour(peakHour.hour), perDay: formatDecimal(peakHour.avgPatientsPerDay) }),
                hint: intl.formatMessage({ id: 'ServicesStatisticsPeakHourHint' }) }
            : null;

    return (
        <section className="nv-section" aria-labelledby="ss-hourly-title">
            <div className="ss-card-head ss-card-head--row">
                <h2 className="nv-section-title" id="ss-hourly-title">{intl.formatMessage({ id: VIEW_TITLE_ID[view] })}</h2>
                <div className="ss-chart-controls">
                    <MeasureSelector id="ss-hourly-measure" labelId="ServicesStatisticsMeasureBy" value={measure}
                        onChange={onMeasureChange} />
                    <div className="ss-measure">
                        <span id="ss-hourly-view" className="nv-sr-only">{intl.formatMessage({ id: 'ServicesStatisticsHourlyViewBy' })}</span>
                        <SelectButton
                            value={view}
                            options={viewOptions}
                            optionLabel="label"
                            optionValue="value"
                            onChange={e => { if (e.value) setView(e.value); }}
                            aria-labelledby="ss-hourly-view"
                        />
                    </div>
                </div>
            </div>
            {sentence && (
                <p className="nv-note">
                    <i className="pi pi-info-circle" aria-hidden="true" />
                    <span>{sentence.text}</span>
                    <InfoTip text={sentence.hint} />
                </p>
            )}
            {view === 'heat'
                ? <HeatmapChart cells={heat} measure={measure} description={description}
                    ariaLabel={intl.formatMessage({ id: 'ServicesStatisticsHeatAria' }, { measure: measureName })} />
                : <HourlyChart points={points} measure={measure} description={description}
                    ariaLabel={intl.formatMessage({ id: 'ServicesStatisticsHourlyAria' }, { measure: measureName })} />}
        </section>
    );
}
